import {
    Popover as ShadcnPopover,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { HintText } from "../input/hint-text";
import { Label } from "../input/label";
import { Popover } from "./popover";
import {
    type CommonProps,
    SelectContext,
    type SelectItemType,
    type SelectKey,
    sizes,
} from "./select-shared";
import { cx } from "@/utils/cx";
import { requiredFieldRingWhen } from "@/domains/shared/services/formFieldStyles";
import { isReactComponent } from "@/utils/is-react-component";
import { Search as SearchLg } from "lucide-react";
import type { FC, ReactNode } from "react";
import { Fragment, isValidElement, useRef, useState } from "react";

interface ComboBoxProps extends CommonProps {
    shortcut?: boolean;
    items?: SelectItemType[];
    popoverClassName?: string;
    shortcutClassName?: string;
    icon?: FC | ReactNode;
    children: ReactNode | ((item: SelectItemType) => ReactNode);
    value?: SelectKey | null;
    defaultValue?: SelectKey | null;
    onValueChange?: (key: SelectKey | null) => void;
    disabled?: boolean;
    required?: boolean;
    invalid?: boolean;
    className?: string;
    "aria-label"?: string;
    "data-testid"?: string;
}

export const ComboBox = ({
    placeholder = "Buscar",
    shortcut = true,
    size = "md",
    children,
    items = [],
    label,
    hint,
    tooltip,
    shortcutClassName,
    icon: Icon = SearchLg,
    hideRequiredIndicator,
    popoverClassName,
    value,
    defaultValue,
    onValueChange,
    disabled,
    required,
    invalid,
    className,
    "aria-label": ariaLabel,
    "data-testid": dataTestId,
}: ComboBoxProps) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const suppressOpenOnFocusRef = useRef(false);
    const [uncontrolledKey, setUncontrolledKey] = useState<SelectKey | null>(
        defaultValue ?? null,
    );
    const currentKey = value !== undefined ? value : uncontrolledKey;
    const selectedItem = items.find((item) => item.id === currentKey);
    const filteredItems = items.filter((item) =>
        `${item.label ?? ""} ${item.supportingText ?? ""}`
            .toLocaleLowerCase("pt-BR")
            .includes(query.toLocaleLowerCase("pt-BR")),
    );
    const renderedItems =
        typeof children === "function"
            ? filteredItems.map((item) => (
                <Fragment key={item.id}>{children(item)}</Fragment>
            ))
            : children;

    const handleSelect = (key: SelectKey) => {
        suppressOpenOnFocusRef.current = true;
        setUncontrolledKey(key);
        onValueChange?.(key);
        setQuery("");
        setOpen(false);
        requestAnimationFrame(() => {
            inputRef.current?.blur();
        });
    };

    const handleOpenChange = (
        nextOpen: boolean,
        eventDetails?: { reason?: string },
    ) => {
        // PopoverTrigger toggles on pointer down while onFocus also opens the list,
        // which causes open → close → open flicker on the combobox input.
        if (eventDetails?.reason === "trigger-press") {
            return;
        }
        if (nextOpen && suppressOpenOnFocusRef.current) {
            suppressOpenOnFocusRef.current = false;
            return;
        }
        setOpen(nextOpen);
    };

    return (
        <SelectContext.Provider
            value={{
                size,
                mode: "multiple",
                selectedKeys: currentKey == null ? new Set() : new Set([currentKey]),
                onItemSelect: handleSelect,
            }}
        >
            <div className={cx("flex flex-col gap-1.5", className)} data-testid={dataTestId}>
                {label ? (
                    <Label isRequired={hideRequiredIndicator ? false : required} tooltip={tooltip}>
                        {label}
                    </Label>
                ) : null}

                <ShadcnPopover open={open} onOpenChange={handleOpenChange}>
                    <div
                        className={cx(
                            "relative flex w-full items-center gap-2 rounded-lg bg-background shadow-xs ring-1 ring-inset",
                            sizes[size].height,
                            requiredFieldRingWhen(required, currentKey == null || currentKey === ""),
                            "focus-within:ring-2 focus-within:ring-ring",
                            "*:data-icon:text-muted-foreground",
                            disabled && "cursor-not-allowed opacity-50",
                            sizes[size].root,
                        )}
                    >
                        {isReactComponent(Icon) ? (
                            <Icon data-icon aria-hidden="true" />
                        ) : isValidElement(Icon) ? (
                            Icon
                        ) : null}
                        <PopoverTrigger
                            render={
                                <Input
                                    ref={inputRef}
                                    disabled={disabled}
                                    required={required}
                                    aria-invalid={invalid || undefined}
                                    aria-label={ariaLabel ?? label}
                                    role="combobox"
                                    aria-expanded={open}
                                    value={open ? query : selectedItem?.label ?? ""}
                                    placeholder={placeholder}
                                    onFocus={() => {
                                        if (suppressOpenOnFocusRef.current) {
                                            suppressOpenOnFocusRef.current = false;
                                            return;
                                        }
                                        setQuery("");
                                        setOpen(true);
                                    }}
                                    onChange={(event) => {
                                        setQuery(event.target.value);
                                        setOpen(true);
                                    }}
                                    className={cx(
                                        "h-auto flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0",
                                        sizes[size].text,
                                    )}
                                />
                            }
                        />
                        {shortcut ? (
                            <span
                                aria-hidden="true"
                                className={cx(
                                    "hidden rounded px-1 py-px text-xs font-medium text-muted-foreground ring-1 ring-border ring-inset md:block",
                                    shortcutClassName,
                                )}
                            >
                                ⌘K
                            </span>
                        ) : null}
                    </div>

                    <Popover
                        size={size}
                        role="listbox"
                        aria-label={label ?? "Opções"}
                        className={popoverClassName}
                    >
                        <div className="flex flex-col gap-0.5">
                            {renderedItems}
                            {filteredItems.length === 0 ? (
                                <p className="px-3 py-2 text-sm text-muted-foreground">
                                    Nenhum resultado encontrado
                                </p>
                            ) : null}
                        </div>
                    </Popover>
                </ShadcnPopover>

                {hint ? (
                    <HintText isInvalid={invalid} className={cx(size === "sm" && "text-xs")}>
                        {hint}
                    </HintText>
                ) : null}
            </div>
        </SelectContext.Provider>
    );
};
